import { parsePrefix } from "@/utils/helpers"

import { Filter } from "./Pageable/Filter"

import type { GoalStatus } from "./Goal"
import type { MilestoneStatus } from "./Milestone"
import type { TeamType } from "./Team"

export type RiskLevel = "GREEN" | "YELLOW" | "RED" | "GREY"

export const creationProgressTypes = [
  "ADD_TEAM",
  "SELECT_TEAM",
  "TEAM_SETUP",
  "TEAM_SETTINGS",
  "CREATED",
  "CREATE_TEAM",
  "START_JIRA_IMPORT_TEAM_SETUP",
  "SETUP_JIRA_IMPORT_ON_TEAM_SETUP",
  "REVIEW_JIRA_IMPORT_TEAM",
  "JIRA_IMPORT_BACKLOG"
]

export const multiProjectPermission = [
  "VISIBLE_WITHOUT_PERMISSIONS",
  "HIDE_ITEM_DETAILS",
  "DO_NOT_DISPLAY_BACKLOG_ITEMS"
]

export enum EstimationMethodology {
  ALL_AVAILABLE = "ALL_AVAILABLE",
  VELOCITY_AND_CAPACITY = "VELOCITY_AND_CAPACITY",
  VELOCITY_BASED_ONLY = "VELOCITY_BASED_ONLY",
  CAPACITY_BASED_ONLY = "CAPACITY_BASED_ONLY",
  NO_ESTIMATES = "NO_ESTIMATES"
}

export type CreationProgress = (typeof creationProgressTypes)[number]

export type MultiProjectPermission = (typeof multiProjectPermission)[number]

export interface SprintCycleParsed {
  label: string
  length: number
}

export class Project {
  id = ""
  name = ""
  demo = false
  importInProgress = false
  utilizationRatio = 65
  sprintCycle = 2
  sprintCycleParsed: SprintCycleParsed = this.sprintCycleParsedByLength(this.sprintCycle)
  maxCritical = 5
  maxHigh = 10
  maxMedium = 20
  maxTrivial = 30
  maxIncidents = 1
  maxReliability = 5
  maxSecurity = 15
  maxPerformance = 30
  urlName: null | string = null
  createdDate = ""
  demoResetDate = ""
  lastChange = ""
  fiveWhysThresholdSeverityLevel = "HIGH"
  users = 0
  teams = 0
  items = 0
  teamIds?: string[]
  timesheets = false
  prefix = ""
  creationProgress: CreationProgress | null = null
  estimationMethodology: EstimationMethodology = EstimationMethodology.ALL_AVAILABLE
  multiProjectPermission: MultiProjectPermission = "HIDE_ITEM_DETAILS"
  includedInSubscription = true
  connectedToJira = false
  favorite = false

  constructor(props?: Partial<Project>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.sprintCycleParsed = this.sprintCycleParsedByLength(this.sprintCycle)
    this.prefix = this.prefix ? this.prefix : parsePrefix(this.name)
  }

  static create(props: Project): Project {
    return new Project(props)
  }

  static createDefault(): Project {
    return new Project()
  }

  getJsonObj() {
    const data = {
      id: this.id,
      name: this.name,
      utilizationRatio: this.utilizationRatio,
      sprintCycle: this.sprintCycle,
      maxCritical: this.maxCritical,
      maxHigh: this.maxHigh,
      maxMedium: this.maxMedium,
      maxTrivial: this.maxTrivial,
      maxIncidents: this.maxIncidents,
      maxReliability: this.maxReliability,
      maxSecurity: this.maxSecurity,
      maxPerformance: this.maxPerformance,
      fiveWhysThresholdSeverityLevel: this.fiveWhysThresholdSeverityLevel,
      urlName: this.urlName,
      teamIds: this.teamIds,
      demo: this.demo,
      createdDate: this.createdDate,
      timesheets: this.timesheets,
      prefix: this.prefix,
      estimationMethodology: this.estimationMethodology,
      multiProjectPermission: this.multiProjectPermission
    }

    return data
  }

  sprintCycleParsedByLength(index: number) {
    return { label: `${index} weeks`, length: index }
  }

  get creationInProgress() {
    return this.creationProgress && this.creationProgress !== "CREATED"
  }
}

export class DemoProjects {
  demoProjects: Project[] = []

  constructor(props?: Partial<DemoProjects>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.demoProjects = this.demoProjects.map(p => new Project(p))
  }
}

export class SearchProject extends Filter {
  q = ""
  includeDemo: boolean | null = null
  timesheets: boolean | null = null

  constructor(props?: Partial<SearchProject>) {
    super(props as any)
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    const data = {
      q: this.q,
      includeDemo: this.includeDemo,
      timesheets: this.timesheets
    }
    return super.getJsonObj({
      params: data
    })
  }
}

export class ProjectPreview {
  id = ""
  name = ""
  demo = false
  urlName: null | string = null
  accountId = ""

  constructor(props?: Partial<ProjectPreview>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface ExistsByNameOrUrlName {
  existsByName: boolean
  existsByUrlName: boolean
}

export interface ProjectSearchItem {
  id: string
  name: string
  accountId: string
  accountName: string
}

export interface ChartInfo {
  [key: string]: ChartInfoExtendedData
}

export interface ChartInfoExtendedData {
  value: number
  riskLevel: RiskLevel
}

export interface ChartInfoItem {
  label: string
  date: Date
  dataset: number | null
  riskLevel?: RiskLevel
}

export class ProjectGoalOverview {
  id = ""
  status: GoalStatus = "CURRENT"
  content = ""
  important = ""
  timeline = ""
  videoUrl = ""
  featuresCount = 0
  epicsCount = 0
  itemsCount = 0
  completedItemsCount = 0
  inProgressItemsCount = 0
  notStartedItemsCount = 0
  whyNow = ""

  constructor(props?: Partial<ProjectGoalOverview>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface ProjectMilestone {
  title: string
  endDate: string
  startDate?: string
  id: string
  chartInfo: ChartInfo
  aiChartInfo?: ChartInfo
  capacityHours?: number
  allTimeMetrics: ProjectStats
  projectProgressMetrics: ProjectStats
  cumulativeProgressMetrics: ProjectStats
  projectProgressMetricsByTeamType: ProjectStatsByTeamType
  cumulativeProgressMetricsByTeamType: ProjectStatsByTeamType
  estimatedDelayInDays: number
  status?: MilestoneStatus
}

export interface ProjectMetrics {
  [key: string]: ProjectStats
}

export type ProjectStatsByTeamType = Partial<Record<TeamType, ProjectStats>>

export type ProjectMetricsByTeamType = Partial<Record<TeamType, ProjectMetrics>>

export class ProjectStats {
  bugsClosed = 0
  bugsOpened = 0
  storiesCompleted = 0
  storiesRemaining = 0
  supportCaseFiled = 0
  supportCaseResolved = 0
  techDebtAdded = 0
  techDebtDone = 0
  designDebtAdded = 0
  designDebtDone = 0
  devOpsOpened = 0
  devOpsClosed = 0
  devOpened = 0
  devClosed = 0
  opsOpened = 0
  opsClosed = 0
  incidentClosed = 0
  incidentOpened = 0

  constructor(props?: Partial<ProjectStats>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface ProjectInfoStats {
  items: number
  teams: number
  users: number
  metricsLog: ProjectMetrics
  metricsLogByTeamType: ProjectMetricsByTeamType
  last90DaysMetrics: ProjectStats
  last90DaysMetricsByTeamType: ProjectStatsByTeamType
  allTimeMetrics: ProjectStats
  allTimeMetricsByTeamType: ProjectStatsByTeamType
  teamActivity: Partial<Record<TeamType, ProjectTeamActivity>>
}

export interface ProjectTeamActivity {
  hasActivityLast90Days: boolean
  lastActivityDate: string | null
  dashboardHidden: boolean
  teamExistsInProject: boolean
}

export class ProjectOverview {
  project: Project
  milestone: ProjectMilestone
  stats: ProjectInfoStats
  goal: ProjectGoalOverview

  constructor(props?: Partial<ProjectOverview>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.project = new Project(this.project)
    this.goal = new ProjectGoalOverview(this.goal)
  }
}

export class ProjectInfo {
  name = ""
  urlName = ""
  projectId = ""
  demo = false
  estimatedDelay = 0
  createdDate = ""
  demoResetDate = ""
  milestone: ProjectMilestone = {
    title: "",
    endDate: "",
    startDate: "",
    id: "",
    chartInfo: {},
    allTimeMetrics: new ProjectStats(),
    projectProgressMetrics: new ProjectStats(),
    cumulativeProgressMetrics: new ProjectStats(),
    projectProgressMetricsByTeamType: {},
    cumulativeProgressMetricsByTeamType: {},
    estimatedDelayInDays: 0
  }
  stats: ProjectInfoStats = {
    items: 0,
    teams: 0,
    users: 0,
    metricsLog: {},
    metricsLogByTeamType: {},
    last90DaysMetrics: new ProjectStats(),
    last90DaysMetricsByTeamType: {},
    allTimeMetrics: new ProjectStats(),
    allTimeMetricsByTeamType: {},
    teamActivity: {}
  }
  creationProgress: CreationProgress | null = null
  goal: ProjectGoalOverview = new ProjectGoalOverview()
  favorite = false

  constructor(props?: Partial<ProjectInfo>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.goal = new ProjectGoalOverview(this.goal)
  }

  get creationInProgress() {
    return this.creationProgress && this.creationProgress !== "CREATED"
  }
}

export interface ProjectWithStats {
  project: Project
  stats: {
    users: number
    teams: number
    items: number
  }
}
